import * as i0 from '@angular/core';
import { EventEmitter, SimpleChange, Directive, Input, Output, Component, ContentChild, HostBinding, Injectable, ViewEncapsulation, HostListener, ViewChildren, Inject, NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { dashboardElements, PropertyChangeEvent, HelpSystemEvent, PropertyStructureChangeEvent, AddBindingDialogEvent, RemoveBindingFromPropertyEvent, AngularDashboardElement, angularDefinitionProviders, angularDashboardElementComponents, angularPropertiesComponents, ElementIssueEvent, BindingValueChangedEvent } from '@sick-curie/dashboard-builder-ui-models';
import { TextField, TextArea, Checkbox, NumericField, DropDown, ToggleGroup } from '@sick-davinci/basic-elements';
import { fromEvent, defer, Subject, BehaviorSubject, combineLatest, of, firstValueFrom, interval, merge as merge$1, EMPTY } from 'rxjs';
import { debounceTime, tap, map, switchMap, finalize, catchError, startWith, mergeMap, isEmpty, delayWhen, repeat, scan, groupBy, distinctUntilChanged, withLatestFrom, retryWhen, delay } from 'rxjs/operators';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import { __decorate } from 'tslib';
import { DEFAULT_PALLETE, DashboardPermission } from '@sick-curie/dashboard-builder-models';
import * as i1 from '@angular/material/snack-bar';
import { MAT_SNACK_BAR_DATA, MatSnackBarModule } from '@angular/material/snack-bar';
import { animation, style, animate, trigger, transition, useAnimation } from '@angular/animations';
import { createCustomElement } from '@angular/elements';
import { merge, kebabCase } from 'lodash-es';

class ElementPropertyLinkingDirective {
    constructor(element) {
        this.element = element;
        this.linkToIndex = undefined;
        this.currentValueChange = new EventEmitter();
        const { nativeElement } = this.element;
        nativeElement.addEventListener('change', (event) => {
            this.newValue(event.detail);
        });
        if (nativeElement instanceof TextField || nativeElement instanceof TextArea) {
            fromEvent(nativeElement, 'input')
                .pipe(debounceTime(200))
                .subscribe((event) => {
                this.newValue(event.detail);
            });
        }
    }
    get currentValue() {
        return this._value;
    }
    set currentValue(value) {
        this.newValue(value);
    }
    newValue(newValue) {
        const property = dashboardElements.getProperty(this.linkToElement.tag, this.linkToProperty);
        if (!property) {
            throw new Error('Unknown property ' + this.linkToProperty);
        }
        let value = newValue;
        switch (!!property) {
            case property.type === 'number':
                value = Number(value);
                break;
            case property.type === 'number[]' && this.linkToIndex === undefined && !Array.isArray(value):
            case property.type === 'number[][]' && this.linkToIndex !== undefined && !Array.isArray(value):
                value = value
                    .split(',')
                    .map((v) => v.trim())
                    .map(Number);
                break;
            case property.type === 'string[]' && this.linkToIndex === undefined && !Array.isArray(value):
            case property.type === 'string[][]' && this.linkToIndex !== undefined && !Array.isArray(value):
                value = value
                    .split(',')
                    .map((v) => v.trim())
                    .filter(Boolean);
                break;
            default:
                break;
        }
        this._value = value !== undefined ? value : undefined;
        this.currentValueChange.emit(this._value);
        this.element.nativeElement.dispatchEvent(new PropertyChangeEvent(this.linkToProperty, this.linkToIndex, value));
    }
    ngOnChanges(changes) {
        if ((changes.linkToIndex || changes.linkToElement) && this.linkToElement) {
            const e = this.element.nativeElement;
            setTimeout(() => {
                const property = dashboardElements.getProperty(this.linkToElement.tag, this.linkToProperty);
                let value = this.linkToElement.properties[this.linkToProperty] ?? property?.defaultValue;
                if (this.linkToIndex !== undefined && value !== undefined) {
                    value = value[this.linkToIndex];
                }
                if (e instanceof Checkbox) {
                    e.checked = value;
                }
                else if (e instanceof TextField ||
                    e instanceof NumericField ||
                    e instanceof DropDown ||
                    e instanceof TextArea ||
                    e instanceof ToggleGroup) {
                    e.value = value;
                }
                this.newValue(value);
            });
        }
    }
    triggerChanges(newValue) {
        this.ngOnChanges({ linkToElement: new SimpleChange('', newValue, false) });
    }
}
ElementPropertyLinkingDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: ElementPropertyLinkingDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
ElementPropertyLinkingDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.4", type: ElementPropertyLinkingDirective, selector: "[linkToElement][linkToProperty]", inputs: { linkToElement: "linkToElement", linkToProperty: "linkToProperty", linkToIndex: "linkToIndex", currentValue: "currentValue" }, outputs: { currentValueChange: "currentValueChange" }, exportAs: ["propertyLink"], usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: ElementPropertyLinkingDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[linkToElement][linkToProperty]',
                    exportAs: 'propertyLink',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { linkToElement: [{
                type: Input
            }], linkToProperty: [{
                type: Input
            }], linkToIndex: [{
                type: Input
            }], currentValueChange: [{
                type: Output
            }], currentValue: [{
                type: Input
            }] } });

class HelpStateComponent {
    constructor(elementRef, renderer) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.hPos = 'left';
    }
    ngOnInit() {
        if (this.element && this.property) {
            const property = dashboardElements.getProperty(this.element.tag, this.property);
            if (property) {
                this.helptext = property.description;
            }
        }
    }
    ngAfterViewInit() {
        const dialog = this.elementRef.nativeElement.closest('dashboard-builder-modal-dialog');
        if (dialog) {
            dialog.addEventListener(HelpSystemEvent.TYPE, (event) => {
                if (event.detail.show) {
                    this.renderer.addClass(this.elementRef.nativeElement, 'is-visible');
                }
                else {
                    this.renderer.removeClass(this.elementRef.nativeElement, 'is-visible');
                }
            });
            const helpTriggerIcon = dialog.querySelector('.dashboard-builder-modal-dialog-help');
            if (helpTriggerIcon) {
                helpTriggerIcon.classList.add('is-visible');
            }
        }
    }
}
HelpStateComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: HelpStateComponent, deps: [{ token: i0.ElementRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
HelpStateComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: HelpStateComponent, selector: "[helptext]", inputs: { element: "element", property: "property", helptext: "helptext", hPos: "hPos" }, ngImport: i0, template: "<ng-content></ng-content>\n<div class=\"help-overlay-icon\" [class]=\"hPos\" [attr.data-title]=\"helptext\">?</div>\n", styles: [":host{display:inline-flex;align-items:center;position:relative}:host.is-visible .help-overlay-icon{visibility:visible}:host .help-overlay-icon{z-index:23;color:#fff;font-family:Arial,Helvetica,sans-serif;background:rgba(243,146,0,.8);position:absolute;top:-7px;bottom:0;text-align:center;font-size:12px;font-weight:400;line-height:14px;width:11px;height:16px;padding-left:5px;border-radius:8px;right:7px;display:flex;flex-wrap:nowrap;align-items:center;-webkit-user-select:none;user-select:none;visibility:hidden}:host .help-overlay-icon:hover:after{box-shadow:0 0 5px #0000004d;background:#fef9e7;border:1px solid #f5a833;border-radius:3px;bottom:26px;color:#000;content:attr(data-title);left:-5px;padding:6px;position:absolute;text-align:left;width:max-content;max-width:160px;z-index:98}:host .help-overlay-icon:hover:before{border:solid;border-color:#f5a833 transparent;border-width:6px 6px 0 6px;bottom:20px;content:\"\";left:3px;position:absolute;z-index:99}:host .help-overlay-icon:hover.right:after{left:auto;right:-5px}\n"] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: HelpStateComponent, decorators: [{
            type: Component,
            args: [{ selector: '[helptext]', template: "<ng-content></ng-content>\n<div class=\"help-overlay-icon\" [class]=\"hPos\" [attr.data-title]=\"helptext\">?</div>\n", styles: [":host{display:inline-flex;align-items:center;position:relative}:host.is-visible .help-overlay-icon{visibility:visible}:host .help-overlay-icon{z-index:23;color:#fff;font-family:Arial,Helvetica,sans-serif;background:rgba(243,146,0,.8);position:absolute;top:-7px;bottom:0;text-align:center;font-size:12px;font-weight:400;line-height:14px;width:11px;height:16px;padding-left:5px;border-radius:8px;right:7px;display:flex;flex-wrap:nowrap;align-items:center;-webkit-user-select:none;user-select:none;visibility:hidden}:host .help-overlay-icon:hover:after{box-shadow:0 0 5px #0000004d;background:#fef9e7;border:1px solid #f5a833;border-radius:3px;bottom:26px;color:#000;content:attr(data-title);left:-5px;padding:6px;position:absolute;text-align:left;width:max-content;max-width:160px;z-index:98}:host .help-overlay-icon:hover:before{border:solid;border-color:#f5a833 transparent;border-width:6px 6px 0 6px;bottom:20px;content:\"\";left:3px;position:absolute;z-index:99}:host .help-overlay-icon:hover.right:after{left:auto;right:-5px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }]; }, propDecorators: { element: [{
                type: Input
            }], property: [{
                type: Input
            }], helptext: [{
                type: Input
            }], hPos: [{
                type: Input
            }] } });

class BindingStateComponent {
    constructor(el) {
        this.el = el;
        this.bound = false;
        this.handleStructureChange = (ev) => {
            if (this.bound && ev.detail.some((d) => d.type === 'remove' && d.index === this.linkToIndex)) {
                this.bound = false;
            }
        };
    }
    ngAfterContentInit() {
        this.linkToElement = this.propertyLink?.linkToElement;
        this.linkToProperty = this.propertyLink?.linkToProperty;
        this.linkToIndex = this.propertyLink?.linkToIndex;
        this.isMandatory = dashboardElements.getProperty(this.linkToElement?.tag, this.linkToProperty)?.mandatory ?? false;
        this.helpText = this.isMandatory
            ? 'This binding is mandatory, click to open the binding dialog and set the binding for this attribute.'
            : 'Click to open the binding dialog and set the binding for this attribute.';
        this.el.nativeElement
            .closest('dashboard-builder-add-relation')
            ?.addEventListener(PropertyStructureChangeEvent.TYPE, this.handleStructureChange);
    }
    ngOnDestroy() {
        this.el.nativeElement
            .closest('dashboard-builder-add-relation')
            ?.removeEventListener(PropertyStructureChangeEvent.TYPE, this.handleStructureChange);
    }
    bindingDialogClicked() {
        this.el.nativeElement.dispatchEvent(new AddBindingDialogEvent({ name: this.linkToProperty, index: this.linkToIndex }));
    }
    overlayClosed() {
        this.bound = false;
        this.el.nativeElement.dispatchEvent(new RemoveBindingFromPropertyEvent({ name: this.linkToProperty, index: this.linkToIndex }));
    }
}
BindingStateComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: BindingStateComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
BindingStateComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: BindingStateComponent, selector: "dashboard-builder-bindable", inputs: { bound: "bound", overlayText: "overlayText" }, host: { properties: { "class.is-selected": "this.bound" } }, queries: [{ propertyName: "propertyLink", first: true, predicate: ElementPropertyLinkingDirective, descendants: true, read: ElementPropertyLinkingDirective }], ngImport: i0, template: "<ng-content></ng-content>\n\n<div class=\"icon-wrapper\" [helptext]=\"helpText\" hPos=\"right\">\n  <davinci-icon\n    icon=\"action/settings\"\n    class=\"icon unbound binding-icon\"\n    [class.mandatory]=\"isMandatory\"\n    (click)=\"bindingDialogClicked()\"\n  >\n  </davinci-icon>\n</div>\n<div class=\"binding-overlay\">\n  <davinci-icon\n    icon=\"action/settings\"\n    class=\"icon bound\"\n    title=\"Edit binding\"\n    (click)=\"bindingDialogClicked()\"\n  ></davinci-icon>\n  <div class=\"binding-text\" [title]=\"overlayText\">{{ overlayText }}</div>\n  <davinci-icon icon=\"navigation/close\" class=\"icon\" title=\"Delete binding\" (click)=\"overlayClosed()\"></davinci-icon>\n</div>\n", styles: [":host{display:inline-flex;align-items:center;position:relative}:host>.help-wrapper .help-overlay-icon{right:-30px}:host.is-selected>.help-wrapper .help-overlay-icon{right:unset;left:30px}:host.is-selected .binding-overlay{visibility:visible}:host.is-selected .binding-icon{visibility:hidden}:host.is-selected .icon-wrapper{display:none}:host .binding-overlay{z-index:20;position:absolute;background-color:#e5f1f8;border:1px solid #bcc7cd;top:0;bottom:0;left:0;right:0;visibility:hidden;display:flex;flex-wrap:nowrap;align-items:center}:host .binding-overlay .binding-text{flex-grow:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding-left:5px}:host .icon-wrapper{position:absolute;top:0;right:-36px;display:flex;height:100%;margin-left:auto;width:36px}:host .icon{cursor:pointer;fill:#6a7a88;margin:auto 5px}:host .icon.mandatory{fill:#f39200}:host .icon.bound{fill:#007cc1!important}:host .icon.unbound{margin:auto}\n"], components: [{ type: HelpStateComponent, selector: "[helptext]", inputs: ["element", "property", "helptext", "hPos"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: BindingStateComponent, decorators: [{
            type: Component,
            args: [{ selector: `dashboard-builder-bindable`, template: "<ng-content></ng-content>\n\n<div class=\"icon-wrapper\" [helptext]=\"helpText\" hPos=\"right\">\n  <davinci-icon\n    icon=\"action/settings\"\n    class=\"icon unbound binding-icon\"\n    [class.mandatory]=\"isMandatory\"\n    (click)=\"bindingDialogClicked()\"\n  >\n  </davinci-icon>\n</div>\n<div class=\"binding-overlay\">\n  <davinci-icon\n    icon=\"action/settings\"\n    class=\"icon bound\"\n    title=\"Edit binding\"\n    (click)=\"bindingDialogClicked()\"\n  ></davinci-icon>\n  <div class=\"binding-text\" [title]=\"overlayText\">{{ overlayText }}</div>\n  <davinci-icon icon=\"navigation/close\" class=\"icon\" title=\"Delete binding\" (click)=\"overlayClosed()\"></davinci-icon>\n</div>\n", styles: [":host{display:inline-flex;align-items:center;position:relative}:host>.help-wrapper .help-overlay-icon{right:-30px}:host.is-selected>.help-wrapper .help-overlay-icon{right:unset;left:30px}:host.is-selected .binding-overlay{visibility:visible}:host.is-selected .binding-icon{visibility:hidden}:host.is-selected .icon-wrapper{display:none}:host .binding-overlay{z-index:20;position:absolute;background-color:#e5f1f8;border:1px solid #bcc7cd;top:0;bottom:0;left:0;right:0;visibility:hidden;display:flex;flex-wrap:nowrap;align-items:center}:host .binding-overlay .binding-text{flex-grow:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding-left:5px}:host .icon-wrapper{position:absolute;top:0;right:-36px;display:flex;height:100%;margin-left:auto;width:36px}:host .icon{cursor:pointer;fill:#6a7a88;margin:auto 5px}:host .icon.mandatory{fill:#f39200}:host .icon.bound{fill:#007cc1!important}:host .icon.unbound{margin:auto}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { propertyLink: [{
                type: ContentChild,
                args: [ElementPropertyLinkingDirective, { read: ElementPropertyLinkingDirective }]
            }], bound: [{
                type: HostBinding,
                args: ['class.is-selected']
            }, {
                type: Input
            }], overlayText: [{
                type: Input
            }] } });

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
const MESSAGE_NAME_RUN = 'function-run';
const MESSAGE_NAME_CHECK = 'function-check';
class FunctionExecutionService {
    constructor() {
        this.listenersMap = new Map();
        this.iframe = document.getElementById(FunctionExecutionService.IFRAME_ID);
        if (this.iframe instanceof HTMLIFrameElement) {
            return;
        }
        const nonce = String(Math.random()).slice(2);
        const iframeScript = () => {
            window.addEventListener('message', function (e) {
                let resultData;
                try {
                    const fn = Function(`return ${e.data.code}`)();
                    if (e.data.name === 'function-check') {
                        resultData = {
                            type: 'success',
                            result: typeof fn === 'function',
                        };
                    }
                    else {
                        const result = fn(...e.data.params);
                        resultData = {
                            type: 'success',
                            result,
                        };
                    }
                }
                catch (err) {
                    resultData = {
                        type: 'error',
                        err,
                    };
                }
                window.parent.postMessage({
                    name: e.data.name,
                    id: e.data.id,
                    data: resultData,
                }, '*');
            });
        };
        this.iframe = document.createElement('iframe');
        this.iframe.setAttribute('id', FunctionExecutionService.IFRAME_ID);
        this.iframe.setAttribute('src', 'about:blank');
        this.iframe.setAttribute('sandbox', 'allow-scripts');
        this.iframe.srcdoc = `
      <head>
        <meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'nonce-${nonce}' 'unsafe-eval'; navigate-to 'none'">
      </head>
      <script nonce="${nonce}">
        var MESSAGE_NAME_RUN = '${MESSAGE_NAME_RUN}';
        var MESSAGE_NAME_CHECK = '${MESSAGE_NAME_CHECK}';
        (${iframeScript.toString()})();
      </script>`;
        this.iframe.style.display = 'none';
        document.body.appendChild(this.iframe);
        window.addEventListener('message', (ev) => {
            if (ev.data?.name !== MESSAGE_NAME_CHECK && ev.data?.name !== MESSAGE_NAME_RUN) {
                return;
            }
            const { id, data } = ev.data;
            const cb = this.listenersMap.get(id);
            if (cb) {
                cb.next(data);
            }
        });
    }
    isValid(code) {
        return defer(() => {
            const id = FunctionExecutionService.REQUEST_ID++;
            const subject = new Subject();
            this.listenersMap.set(id, subject);
            this.iframe.contentWindow.postMessage({
                name: MESSAGE_NAME_CHECK,
                id,
                code,
            }, '*');
            return subject.pipe(tap(() => {
                this.listenersMap.delete(id);
            }), map((data) => (data.type === 'success' ? Boolean(data.result) : false)));
        });
    }
    run(code) {
        const id = FunctionExecutionService.REQUEST_ID++;
        const subject = new Subject();
        this.listenersMap.set(id, subject);
        return (source$) => source$.pipe(tap((params) => {
            this.iframe.contentWindow.postMessage({
                name: MESSAGE_NAME_RUN,
                id,
                code,
                params,
            }, '*');
        }), switchMap(() => subject), map((data) => {
            if (data.type === 'success') {
                return data.result;
            }
            throw data.err;
        }), finalize(() => {
            this.listenersMap.delete(id);
        }));
    }
}
FunctionExecutionService.REQUEST_ID = 1;
FunctionExecutionService.IFRAME_ID = 'dashboard-builder-function-execution-service-iframe';
FunctionExecutionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: FunctionExecutionService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FunctionExecutionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: FunctionExecutionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: FunctionExecutionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return []; } });

/**
 * Scrolls an element into view after creation.
 * Useful on elements wrapped in a *ngIf that should be scrolled into view once the condition is met.
 */
class ScrollIntoViewDirective {
    constructor(el) {
        this.el = el;
    }
    ngOnInit() {
        this.el.nativeElement?.scrollIntoView();
    }
}
ScrollIntoViewDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: ScrollIntoViewDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
ScrollIntoViewDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.4", type: ScrollIntoViewDirective, selector: "[scrollIntoView]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: ScrollIntoViewDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: '[scrollIntoView]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; } });

class FunctionExecutionResultComponent {
    constructor(functionExecutionService) {
        this.functionExecutionService = functionExecutionService;
        this.value$ = new BehaviorSubject(undefined);
        this.valueProcessor$ = new BehaviorSubject(undefined);
        this.evaluatedResult = combineLatest([this.value$, this.valueProcessor$]).pipe(switchMap(([value, valueProcessor]) => of(value).pipe(switchMap(async (value) => {
            if (value === undefined) {
                return undefined;
            }
            if (!valueProcessor) {
                return { type: 'data', value };
            }
            const funIsValid = await firstValueFrom(this.functionExecutionService.isValid(valueProcessor));
            if (!funIsValid) {
                return undefined;
            }
            try {
                const result = await firstValueFrom(of([value]).pipe(this.functionExecutionService.run(valueProcessor)));
                return { type: 'data', value: result };
            }
            catch (err) {
                throw new Error(FunctionExecutionResultComponent.ERROR_FUNCTION_RUNTIME);
            }
        }), catchError((e) => of({
            type: 'error',
            msg: e.message,
        })))), map((result) => result && result.type === 'data' && result.value === undefined
            ? { type: 'data', value: 'undefined' }
            : result));
    }
    set value(data) {
        this.value$.next(data);
    }
    set valueProcessor(processor) {
        this.valueProcessor$.next(processor);
    }
}
FunctionExecutionResultComponent.ERROR_FUNCTION_SYNTAX = 'The function you entered is not valid.\n\nPlease make sure there are no typos or JavaScript syntax errors.';
FunctionExecutionResultComponent.ERROR_FUNCTION_RUNTIME = 'A Runtime error occurred. Please check and adapt the function.';
FunctionExecutionResultComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: FunctionExecutionResultComponent, deps: [{ token: FunctionExecutionService }], target: i0.ɵɵFactoryTarget.Component });
FunctionExecutionResultComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: FunctionExecutionResultComponent, selector: "dashboard-builder-post-processor-result", inputs: { value: "value", valueProcessor: "valueProcessor" }, ngImport: i0, template: "<div *ngIf=\"evaluatedResult | async as result\" class=\"execution-output\">\n  <div class=\"label\">Result</div>\n  <code scrollIntoView *ngIf=\"result.type === 'data'; else functionError\">\n    <pre>{{ result.value | json }}</pre>\n  </code>\n  <ng-template #functionError>\n    <davinci-callout type=\"warning\" *ngIf=\"result.type === 'error'\">{{ result.msg }}</davinci-callout>\n  </ng-template>\n</div>\n<div>\n  <ng-content></ng-content>\n</div>\n", styles: [".execution-output code{height:80px;display:block;overflow-y:auto;background:#f0f0f0;padding:0 15px;border:1px dashed #aaa}\n"], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: ScrollIntoViewDirective, selector: "[scrollIntoView]" }], pipes: { "async": i2.AsyncPipe, "json": i2.JsonPipe } });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: FunctionExecutionResultComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-builder-post-processor-result', template: "<div *ngIf=\"evaluatedResult | async as result\" class=\"execution-output\">\n  <div class=\"label\">Result</div>\n  <code scrollIntoView *ngIf=\"result.type === 'data'; else functionError\">\n    <pre>{{ result.value | json }}</pre>\n  </code>\n  <ng-template #functionError>\n    <davinci-callout type=\"warning\" *ngIf=\"result.type === 'error'\">{{ result.msg }}</davinci-callout>\n  </ng-template>\n</div>\n<div>\n  <ng-content></ng-content>\n</div>\n", styles: [".execution-output code{height:80px;display:block;overflow-y:auto;background:#f0f0f0;padding:0 15px;border:1px dashed #aaa}\n"] }]
        }], ctorParameters: function () { return [{ type: FunctionExecutionService }]; }, propDecorators: { value: [{
                type: Input
            }], valueProcessor: [{
                type: Input
            }] } });

// this is only here because it needs to be defined somewhere
let GridTileComponent = class GridTileComponent {
    constructor() {
        this.x = 0;
        this.y = 0;
        this.rows = 0;
        this.cols = 0;
        this.header = '';
        this.lock = false;
    }
};
GridTileComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: GridTileComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
GridTileComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: GridTileComponent, selector: "dashboard-grid-tile", inputs: { x: "x", y: "y", rows: "rows", cols: "cols", header: "header", lock: "lock" }, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, encapsulation: i0.ViewEncapsulation.ShadowDom });
GridTileComponent = __decorate([
    AngularDashboardElement({
        role: 'structure',
        documentation: {
            name: 'Grid tile',
            description: '',
        },
    })
], GridTileComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: GridTileComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'dashboard-grid-tile',
                    template: '<ng-content></ng-content>',
                    encapsulation: ViewEncapsulation.ShadowDom,
                }]
        }], propDecorators: { x: [{
                type: Input
            }], y: [{
                type: Input
            }], rows: [{
                type: Input
            }], cols: [{
                type: Input
            }], header: [{
                type: Input
            }], lock: [{
                type: Input
            }] } });

class SpecializedBindingPropertiesComponent {
    constructor() {
        this.propertyChange = new EventEmitter();
        this.hasError = new EventEmitter();
        this.propertyChanged$ = new Subject();
        this.propertyChanged$.pipe(debounceTime(500)).subscribe(() => {
            this.hasError.emit(this.formHasErrors());
        });
    }
    onPropertyChange() {
        this.propertyChanged$.next();
    }
    formHasErrors() {
        return false;
    }
}
SpecializedBindingPropertiesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: SpecializedBindingPropertiesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SpecializedBindingPropertiesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: SpecializedBindingPropertiesComponent, selector: "ng-component", inputs: { typeHint: "typeHint", binding: "binding" }, outputs: { propertyChange: "propertyChange", hasError: "hasError" }, host: { listeners: { "propertyChange": "onPropertyChange()", "propertyStructureChange": "onPropertyChange()" } }, ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: SpecializedBindingPropertiesComponent, decorators: [{
            type: Component,
            args: [{
                    template: '',
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { typeHint: [{
                type: Input
            }], binding: [{
                type: Input
            }], propertyChange: [{
                type: Output
            }], hasError: [{
                type: Output
            }], onPropertyChange: [{
                type: HostListener,
                args: [PropertyChangeEvent.TYPE]
            }, {
                type: HostListener,
                args: [PropertyStructureChangeEvent.TYPE]
            }] } });

class SpecializedElementPropertiesComponent {
    constructor() {
        this.generalPalette = DEFAULT_PALLETE;
        this.propertyChange = new EventEmitter();
        this.propertyStructureChange = new EventEmitter();
        this.addBindingDialog = new EventEmitter();
        this.removeBindingFromProperty = new EventEmitter();
        this.bindings$ = new BehaviorSubject([]);
    }
    set bindings(bindings) {
        this.bindings$.next(bindings);
    }
    get bindings() {
        return this.bindings$.value;
    }
    static updateBoundState(bindings, directives) {
        for (const d of directives) {
            const b = bindings.find((pb) => pb.property.name === d.linkToProperty && pb.property.index === d.linkToIndex);
            if (b) {
                d.bound = true;
                d.overlayText = dashboardElements.getDescription(b.instance.tag).documentation.name;
            }
        }
    }
    ngAfterViewInit() {
        this.sub = combineLatest([
            this.bindings$,
            this.bindingDirectives.changes.pipe(startWith(this.bindingDirectives)),
        ]).subscribe(([bindings, directives]) => setTimeout(() => SpecializedElementPropertiesComponent.updateBoundState(bindings, directives)));
    }
    ngOnDestroy() {
        this.sub?.unsubscribe();
    }
}
SpecializedElementPropertiesComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: SpecializedElementPropertiesComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
SpecializedElementPropertiesComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: SpecializedElementPropertiesComponent, selector: "ng-component", inputs: { element: "element", bindings: "bindings", generalPalette: "generalPalette" }, outputs: { propertyChange: "propertyChange", propertyStructureChange: "propertyStructureChange", addBindingDialog: "addBindingDialog", removeBindingFromProperty: "removeBindingFromProperty" }, viewQueries: [{ propertyName: "bindingDirectives", predicate: BindingStateComponent, descendants: true }], ngImport: i0, template: '', isInline: true });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: SpecializedElementPropertiesComponent, decorators: [{
            type: Component,
            args: [{
                    template: '',
                }]
        }], propDecorators: { element: [{
                type: Input
            }], bindings: [{
                type: Input
            }], generalPalette: [{
                type: Input
            }], propertyChange: [{
                type: Output
            }], propertyStructureChange: [{
                type: Output
            }], addBindingDialog: [{
                type: Output
            }], removeBindingFromProperty: [{
                type: Output
            }], bindingDirectives: [{
                type: ViewChildren,
                args: [BindingStateComponent]
            }] } });

class UserInputComponent {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.showBalloon = false;
    }
    get hintText() {
        return this.error || this.warning || this.info;
    }
    ngOnChanges(changes) {
        if (changes.error || changes.warning) {
            const input = this.elementRef.nativeElement?.querySelector('davinci-text-field, davinci-text-area');
            if (input) {
                input.error = Boolean(this.error);
                input.warning = !this.error && Boolean(this.warning);
            }
        }
    }
}
UserInputComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: UserInputComponent, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component });
UserInputComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: UserInputComponent, selector: "dashboard-builder-user-input", inputs: { error: "error", warning: "warning", info: "info" }, usesOnChanges: true, ngImport: i0, template: "<div class=\"container\" [class.has-hint]=\"hintText\">\n  <ng-content></ng-content>\n  <ng-tempalte *ngIf=\"hintText\">\n    <davinci-status-text\n      [error]=\"!!error\"\n      [warning]=\"!error && !!warning\"\n      (mouseenter)=\"showBalloon = true\"\n      (mouseleave)=\"showBalloon = false\"\n      (click)=\"showBalloon = true\"\n    >\n      <div class=\"text\" [class.warning]=\"!error && !!warning\" [class.info]=\"!error && !warning\">{{ hintText }}</div>\n    </davinci-status-text>\n    <davinci-balloon position=\"bottom\" alignment=\"bottom\" for=\":before\" [show]=\"showBalloon\">\n      <div class=\"balloon-content\">{{ hintText }}</div>\n    </davinci-balloon>\n  </ng-tempalte>\n</div>\n", styles: [".container{position:relative;width:100%}.container.has-hint davinci-status-text{position:absolute;left:0;bottom:-1.3rem;max-width:100%;height:1rem}.container.has-hint .text{font-size:.9rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.container.has-hint .text.warning{color:var(--davinci-color-accent,#F39200)}.container.has-hint .text.info{color:var(--davinci-color-secondary,#737F85)}.container.has-hint .balloon-content{max-width:350px}\n"], directives: [{ type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: UserInputComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-builder-user-input', template: "<div class=\"container\" [class.has-hint]=\"hintText\">\n  <ng-content></ng-content>\n  <ng-tempalte *ngIf=\"hintText\">\n    <davinci-status-text\n      [error]=\"!!error\"\n      [warning]=\"!error && !!warning\"\n      (mouseenter)=\"showBalloon = true\"\n      (mouseleave)=\"showBalloon = false\"\n      (click)=\"showBalloon = true\"\n    >\n      <div class=\"text\" [class.warning]=\"!error && !!warning\" [class.info]=\"!error && !warning\">{{ hintText }}</div>\n    </davinci-status-text>\n    <davinci-balloon position=\"bottom\" alignment=\"bottom\" for=\":before\" [show]=\"showBalloon\">\n      <div class=\"balloon-content\">{{ hintText }}</div>\n    </davinci-balloon>\n  </ng-tempalte>\n</div>\n", styles: [".container{position:relative;width:100%}.container.has-hint davinci-status-text{position:absolute;left:0;bottom:-1.3rem;max-width:100%;height:1rem}.container.has-hint .text{font-size:.9rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}.container.has-hint .text.warning{color:var(--davinci-color-accent,#F39200)}.container.has-hint .text.info{color:var(--davinci-color-secondary,#737F85)}.container.has-hint .balloon-content{max-width:350px}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { error: [{
                type: Input
            }], warning: [{
                type: Input
            }], info: [{
                type: Input
            }] } });

const flyInAnimation = animation([
    style({
        transform: 'translateX(120%)',
    }),
    animate('0.66s cubic-bezier(0.35, 0, 0.25, 1)'),
]);
const flyOutAnimation = animation([
    animate('0.66s cubic-bezier(0.35, 0, 0.25, 1)', style({ transform: 'translateX(120%)' })),
]);
class ToastComponent {
    constructor(data, snackBarRef) {
        this.data = data;
        this.snackBarRef = snackBarRef;
        this.iconPath = (() => {
            switch (data.type) {
                case 'info':
                    return 'action/info';
                case 'success':
                    return 'action/check_circle';
                case 'warning':
                    return 'alert/warning';
                case 'error':
                    return 'alert/error';
                default:
                    return 'action/info';
            }
        })();
    }
    closeToast() {
        this.snackBarRef.dismiss();
    }
    actionButtonClicked() {
        // TODO when this is needed
    }
}
ToastComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: ToastComponent, deps: [{ token: MAT_SNACK_BAR_DATA }, { token: i1.MatSnackBarRef }], target: i0.ɵɵFactoryTarget.Component });
ToastComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.4", type: ToastComponent, selector: "dashboard-builder-toast", ngImport: i0, template: "<div class=\"container\" [ngClass]=\"data.type\">\n  <div class=\"header\">\n    <davinci-icon [icon]=\"iconPath\" class=\"icon-status\"></davinci-icon>\n    <p class=\"title\">{{ data.title }}</p>\n    <davinci-icon icon=\"navigation/close\" class=\"icon-close\" (click)=\"closeToast()\"></davinci-icon>\n  </div>\n  <div class=\"body\">\n    <p class=\"message\">{{ data.message }}</p>\n  </div>\n  <div *ngIf=\"data.buttonLabel\" class=\"action\">\n    <davinci-button class=\"action-button\" type=\"bare-neutral\" (click)=\"actionButtonClicked()\">{{\n      data.buttonLabel\n    }}</davinci-button>\n  </div>\n</div>\n", styles: [":host{height:100%;display:block;margin-bottom:12px;box-shadow:1px 2px 5px 0 var(--davinci-color-mid-gray-2,#B1B8BC)}:host .container{color:var(--davinci-color-dark-gray-2,#2C3133);border-left:solid var(--davinci-color-primary,#007CC1) 4px;padding:16px 16px 16px 12px;border-left-color:var(--davinci-color-information,#007cc1);background-color:var(--davinci-color-information-light,#e5f1f8)}:host .header{display:flex;align-items:center}:host .icon-status{height:24px;width:24px;fill:var(--davinci-color-information,#007cc1)}:host .title{margin:0 16px 0 8px;font-weight:700;color:var(--davinci-color-dark-gray-3,#50585C)}:host .icon-close{height:16px;width:16px;margin-left:auto;cursor:pointer;fill:var(--davinci-color-dark-gray-3,#50585C)}:host .icon-close:hover{fill:var(--davinci-color-dark-gray-2,#2C3133)}:host .body{margin:0 16px 0 32px}:host .message{margin:0 0 2px}:host .action{display:flex;justify-content:flex-end}:host .action-button{height:24px;min-height:24px;margin:0;padding:0;font-size:12px;color:var(--davinci-color-dark-gray-2,#2C3133)}:host .action-button:hover{color:var(--davinci-color-dark-gray-1,#141718)}:host .success{border-left-color:var(--davinci-color-success,#63b017);background-color:var(--davinci-color-success-light,#EFF7E7)}:host .success .icon-status{fill:var(--davinci-color-success,#63b017)}:host .warning{border-left-color:var(--davinci-color-warning,#F5C413);background-color:var(--davinci-color-warning-light,#FEF9E7)}:host .warning .icon-status{fill:var(--davinci-color-warning,#F5C413)}:host .error{border-left-color:var(--davinci-color-error,#EA0823);background-color:var(--davinci-color-error-light,#FCE6E9)}:host .error .icon-status{fill:var(--davinci-color-error,#EA0823)}\n"], directives: [{ type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }], animations: [
        trigger('flyInOut', [
            transition(':enter', [useAnimation(flyInAnimation)]),
            transition(':leave', [useAnimation(flyOutAnimation)]),
        ]),
    ] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: ToastComponent, decorators: [{
            type: Component,
            args: [{ selector: 'dashboard-builder-toast', animations: [
                        trigger('flyInOut', [
                            transition(':enter', [useAnimation(flyInAnimation)]),
                            transition(':leave', [useAnimation(flyOutAnimation)]),
                        ]),
                    ], template: "<div class=\"container\" [ngClass]=\"data.type\">\n  <div class=\"header\">\n    <davinci-icon [icon]=\"iconPath\" class=\"icon-status\"></davinci-icon>\n    <p class=\"title\">{{ data.title }}</p>\n    <davinci-icon icon=\"navigation/close\" class=\"icon-close\" (click)=\"closeToast()\"></davinci-icon>\n  </div>\n  <div class=\"body\">\n    <p class=\"message\">{{ data.message }}</p>\n  </div>\n  <div *ngIf=\"data.buttonLabel\" class=\"action\">\n    <davinci-button class=\"action-button\" type=\"bare-neutral\" (click)=\"actionButtonClicked()\">{{\n      data.buttonLabel\n    }}</davinci-button>\n  </div>\n</div>\n", styles: [":host{height:100%;display:block;margin-bottom:12px;box-shadow:1px 2px 5px 0 var(--davinci-color-mid-gray-2,#B1B8BC)}:host .container{color:var(--davinci-color-dark-gray-2,#2C3133);border-left:solid var(--davinci-color-primary,#007CC1) 4px;padding:16px 16px 16px 12px;border-left-color:var(--davinci-color-information,#007cc1);background-color:var(--davinci-color-information-light,#e5f1f8)}:host .header{display:flex;align-items:center}:host .icon-status{height:24px;width:24px;fill:var(--davinci-color-information,#007cc1)}:host .title{margin:0 16px 0 8px;font-weight:700;color:var(--davinci-color-dark-gray-3,#50585C)}:host .icon-close{height:16px;width:16px;margin-left:auto;cursor:pointer;fill:var(--davinci-color-dark-gray-3,#50585C)}:host .icon-close:hover{fill:var(--davinci-color-dark-gray-2,#2C3133)}:host .body{margin:0 16px 0 32px}:host .message{margin:0 0 2px}:host .action{display:flex;justify-content:flex-end}:host .action-button{height:24px;min-height:24px;margin:0;padding:0;font-size:12px;color:var(--davinci-color-dark-gray-2,#2C3133)}:host .action-button:hover{color:var(--davinci-color-dark-gray-1,#141718)}:host .success{border-left-color:var(--davinci-color-success,#63b017);background-color:var(--davinci-color-success-light,#EFF7E7)}:host .success .icon-status{fill:var(--davinci-color-success,#63b017)}:host .warning{border-left-color:var(--davinci-color-warning,#F5C413);background-color:var(--davinci-color-warning-light,#FEF9E7)}:host .warning .icon-status{fill:var(--davinci-color-warning,#F5C413)}:host .error{border-left-color:var(--davinci-color-error,#EA0823);background-color:var(--davinci-color-error-light,#FCE6E9)}:host .error .icon-status{fill:var(--davinci-color-error,#EA0823)}\n"] }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [MAT_SNACK_BAR_DATA]
                }] }, { type: i1.MatSnackBarRef }]; } });

class DashboardBuilderCoreModule {
}
DashboardBuilderCoreModule.REGISTER = [GridTileComponent];
DashboardBuilderCoreModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderCoreModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
DashboardBuilderCoreModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderCoreModule, declarations: [GridTileComponent,
        SpecializedElementPropertiesComponent,
        SpecializedBindingPropertiesComponent,
        ElementPropertyLinkingDirective,
        FunctionExecutionResultComponent,
        ScrollIntoViewDirective,
        BindingStateComponent,
        HelpStateComponent,
        ToastComponent,
        UserInputComponent], imports: [CommonModule, MatSnackBarModule], exports: [GridTileComponent,
        SpecializedElementPropertiesComponent,
        SpecializedBindingPropertiesComponent,
        ElementPropertyLinkingDirective,
        FunctionExecutionResultComponent,
        ScrollIntoViewDirective,
        BindingStateComponent,
        HelpStateComponent,
        UserInputComponent] });
DashboardBuilderCoreModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderCoreModule, imports: [[CommonModule, MatSnackBarModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: DashboardBuilderCoreModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule, MatSnackBarModule],
                    declarations: [
                        GridTileComponent,
                        SpecializedElementPropertiesComponent,
                        SpecializedBindingPropertiesComponent,
                        ElementPropertyLinkingDirective,
                        FunctionExecutionResultComponent,
                        ScrollIntoViewDirective,
                        BindingStateComponent,
                        HelpStateComponent,
                        ToastComponent,
                        UserInputComponent,
                    ],
                    exports: [
                        GridTileComponent,
                        SpecializedElementPropertiesComponent,
                        SpecializedBindingPropertiesComponent,
                        ElementPropertyLinkingDirective,
                        FunctionExecutionResultComponent,
                        ScrollIntoViewDirective,
                        BindingStateComponent,
                        HelpStateComponent,
                        UserInputComponent,
                    ],
                    schemas: [CUSTOM_ELEMENTS_SCHEMA],
                }]
        }] });

class ElementDefinitionService {
    constructor(injector, resolver) {
        this.injector = injector;
        this.resolver = resolver;
        this.cache$ = new BehaviorSubject([]);
        this.cache$.next(dashboardElements.getDescriptions());
        dashboardElements.registerElementDefinitionCallback((newElement) => this.cache$.next(this.cache$.value.concat(newElement)));
        const providers = angularDefinitionProviders();
        angularDashboardElementComponents().map((component) => {
            const childToParentDefinitions = [];
            let c = component;
            while (providers.has(c)) {
                childToParentDefinitions.push(providers.get(c));
                c = Object.getPrototypeOf(c);
            }
            const def = childToParentDefinitions
                .reverse()
                .reduce((parent, child) => ElementDefinitionService.mergeDefinitions(parent, child), {});
            this.defineAngularDashboardElement(component, def);
        });
        angularPropertiesComponents().forEach((e) => this.defineAngularPropertiesElement(e));
    }
    static mergeDefinitions(parent, child) {
        const parentProperties = parent?.properties?.map((pProperty) => {
            const cProperty = child?.properties?.find((cProperty) => cProperty.name === pProperty.name);
            // all or nothing: if child uses @Describe it will override everything that was set by parent's @Describe
            return cProperty ? cProperty : pProperty;
        }) ?? [];
        const childOnlyProperties = child?.properties?.filter((cProperty) => !parent?.properties?.find((pProperty) => pProperty.name === cProperty.name)) ?? [];
        return { ...child, properties: parentProperties.concat(childOnlyProperties) };
    }
    static generateComponentsJson(elemDef, injector, cf) {
        const c = cf.create(injector);
        const properties = new Array();
        for (const property of cf.inputs) {
            const name = property.propName;
            const existingProperty = elemDef.properties.find((p) => p.name === name);
            const defaultValue = c.instance[name];
            const defaultValueType = existingProperty?.type
                ? existingProperty.type
                : ElementDefinitionService.guessType(defaultValue);
            const mergedProperty = merge({
                name,
                attributeName: kebabCase(name),
                ...(defaultValue !== undefined
                    ? {
                        defaultValue,
                        defaultValueType,
                    }
                    : {}),
                type: defaultValueType,
                description: '',
            }, existingProperty);
            properties.push(mergedProperty);
        }
        const completeElemDef = { ...elemDef };
        completeElemDef.tag = completeElemDef.tag ?? cf.selector;
        completeElemDef.properties = properties;
        // TODO check if it's really complete
        return completeElemDef;
    }
    static guessType(value) {
        if (typeof value !== 'object' && typeof value !== 'undefined') {
            return typeof value;
        }
        let subValue = value;
        let suffix = '';
        while (Array.isArray(subValue)) {
            suffix += '[]';
            subValue = subValue.length ? subValue[0] : undefined;
        }
        const elemType = typeof subValue;
        if (elemType === 'undefined') {
            return 'unknown' + suffix;
        }
        return elemType + suffix;
    }
    get allAvailableElements() {
        return this.cache$;
    }
    get availableUiElements() {
        return this.allAvailableElements.pipe(map((defs) => defs.filter((def) => def.role === 'ui')));
    }
    get availableBindingElements() {
        return this.allAvailableElements.pipe(map((defs) => defs.filter((def) => def.role === 'behavior')));
    }
    /**
     * Will process the given input and try to register it as a dashboard element. This process will fail if:
     * 1. The input is not a component or if it's an abstract class
     * 2. The input is a component but does not define a selector
     *
     * @param component - the component to register
     * @param def - the (partial) definition to apply for metadata (most commonly description)
     */
    defineAngularDashboardElement(component, def) {
        const factory = this.resolver.resolveComponentFactory(component);
        if (factory.selector === 'ng-component') {
            throw new Error("Either no selector is defined, or the component is using the default 'ng-component' selector");
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (factory.componentDef?.encapsulation !== ViewEncapsulation.ShadowDom ?? false) {
            // eslint-disable-next-line no-console
            console.warn(`Element '${factory.selector}' does not have ViewEncapsulation.ShadowDom set. This can cause unexpected issues and is not recommended.`);
        }
        const componentsJson = ElementDefinitionService.generateComponentsJson(def, this.injector, factory);
        const customElementConstructor = createCustomElement(component, { injector: this.injector });
        dashboardElements.define(componentsJson.tag, customElementConstructor, undefined, componentsJson);
    }
    defineAngularPropertiesElement(component) {
        const customElement = createCustomElement(component, { injector: this.injector });
        const factory = this.resolver.resolveComponentFactory(component);
        customElements.define(factory.selector, customElement);
    }
}
ElementDefinitionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: ElementDefinitionService, deps: [{ token: i0.Injector }, { token: i0.ComponentFactoryResolver }], target: i0.ɵɵFactoryTarget.Injectable });
ElementDefinitionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: ElementDefinitionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: ElementDefinitionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.ComponentFactoryResolver }]; } });

/**
 * @copyright
 * Copyright(c) 2021 SICK AG
 */
class NotificationService {
    constructor(snackBar, zone) {
        this.snackBar = snackBar;
        this.zone = zone;
    }
    show(data) {
        this.zone.run(() => {
            this.snackBar.dismiss();
            this.snackBar.openFromComponent(ToastComponent, {
                data,
                duration: 8000,
                panelClass: 'dbb-snackbar',
                verticalPosition: 'bottom',
            });
        });
    }
    showInfo(title, message) {
        this.show({
            title,
            message,
            type: 'info',
        });
    }
    showSuccess(title, message) {
        this.show({
            title,
            message,
            type: 'success',
        });
    }
    showWarning(title, message) {
        this.show({
            title,
            message,
            type: 'warning',
        });
    }
    showError(title, message) {
        this.show({
            title,
            message,
            type: 'error',
        });
    }
    dismiss() {
        this.zone.run(() => {
            this.snackBar.dismiss();
        });
    }
}
NotificationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: NotificationService, deps: [{ token: i1.MatSnackBar }, { token: i0.NgZone }], target: i0.ɵɵFactoryTarget.Injectable });
NotificationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: NotificationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.4", ngImport: i0, type: NotificationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.MatSnackBar }, { type: i0.NgZone }]; } });

/**
 * A typical implementation for querying remote resources periodically uses timer/interval + switchMap/mergeMap,
 * however, both switchMap and mergeMap have drawbacks in case the resource takes longer to query than the
 * desired timer period.
 *
 * Let's say you want an update rate of 1s, but it takes 2s for the resource to be fetched. In this case, the timer will
 * trigger faster than your resource is retrieved, so what happens if you pipe switchMap/mergeMap with the resource
 * Observable into that timer?
 * switchMap: Cancels the previous request. If the query consistently takes longer than 1s, you get a cascade of
 * cancels with no actual data.
 * mergeMap: Queues a new query to be executed before all previous finish. Essentially you're spamming the server and
 * may get data out of order depending on how high the variance in resource retrieval is.
 *
 * This class attempts to retrieve the given resource periodically with the following properties:
 * - only have one query pending at a time
 * - don't queue another query before the last one finishes
 *
 * The result is a timer that attempts to respect the desired update period, but is allowed to slack off when the
 * queries don't finish quickly enough.
 */
class BestEffortSingleQueryTimer {
    constructor(source, targetPeriodMs) {
        this.value$ = new Subject();
        this.roundTripData$ = new Subject();
        this.obs$ = defer(() => of(Date.now())).pipe(mergeMap((startTime) => source.pipe(tap((value) => this.value$.next(value)), 
        // delayWhen is only executed when there's a value, but we also want to delay on empty
        isEmpty(), delayWhen(() => {
            const roundTripTime = Date.now() - startTime;
            this.roundTripData$.next(roundTripTime);
            const delayTime = targetPeriodMs - roundTripTime;
            return interval(delayTime);
        }))), 
        // hack to ensure that this will never emit
        mergeMap(() => of()), repeat());
    }
    periodicData() {
        // the point of the merge is so that a consumer will also unwittingly subscribe to the observable responsible for managing the delay
        return merge$1(this.obs$, this.value$);
    }
    movingAverageRoundTripTime() {
        return this.roundTripData$.pipe(scan((acc, cur) => {
            acc.push(cur);
            if (acc.length > BestEffortSingleQueryTimer.MOVING_AVERAGE_WINDOW) {
                acc.shift();
            }
            return acc;
        }, []), map((arr) => arr.reduce((acc, cur) => acc + cur, 0) / arr.length), 
        // without this it takes two iterations before any data is returned in combination with withLatestFrom
        startWith(0));
    }
}
BestEffortSingleQueryTimer.MOVING_AVERAGE_WINDOW = 10;

/**
 * Instance method operators have error dispatching built in, while the standalone operators don't.
 */
class BindingHelper {
    constructor(elRef, functionExecutionService) {
        this.elRef = elRef;
        this.functionExecutionService = functionExecutionService;
        this.issueStream$ = new Subject();
        this.previousPostProcessedData = undefined;
        this.issueStream$
            .pipe(groupBy((issue) => issue.message), mergeMap((group) => group.pipe(distinctUntilChanged(
        // only propagate if the resolved state changed, or when the details change while not resolved
        (a, b) => (a.resolved && b.resolved) || (!a.resolved && !b.resolved && a.details === b.details)))))
            .subscribe((issue) => this.elRef.nativeElement.dispatchEvent(new ElementIssueEvent(issue)));
    }
    setValue(property, value) {
        this.elRef.nativeElement.dispatchEvent(new BindingValueChangedEvent({ property, value: value }));
        if (this.elRef.nativeElement.parentElement) {
            this.elRef.nativeElement.parentElement[property] = value;
        }
    }
    dispatchWarning(message, details, resolved = false) {
        this.issueStream$.next({ severity: 'warn', message, details, resolved });
    }
    dispatchError(message, details, resolved = false) {
        this.issueStream$.next({ severity: 'error', message, details, resolved });
    }
    /**
     * Useful for continuously polling a resource. The operator will attempt to poll the upstream observable with the
     * provided interval, and dispatch a warning if it doesn't manage to keep up.
     *
     * Errors will be propagated downstream.
     *
     * @param intervalMs - the goal interval for polling
     */
    poll(intervalMs) {
        return (source$) => {
            const bt = new BestEffortSingleQueryTimer(source$, intervalMs);
            return bt.periodicData().pipe(withLatestFrom(bt.movingAverageRoundTripTime()), tap(([, avgRtt]) => this.dispatchWarning(BindingHelper.WARN_UPDATE_PERIOD, `Desired: ${intervalMs}ms, actual: ${Math.floor(avgRtt)}ms`, avgRtt <= intervalMs)), map(([data]) => data));
        };
    }
    /**
     * Returns an object or EMPTY if the parsing fails.
     *
     * Errors will not be propagated downstream.
     */
    parseJson() {
        const op = (source$) => source$.pipe(map((data) => JSON.parse(data)), this.catchErrorAndDispatch(BindingHelper.ERROR_JSON));
        return isolateErrors(op);
    }
    /**
     * Returns a sub-object selected via the selector, or EMPTY if the selector is not valid for that object
     *
     * Errors will not be propagated downstream.
     *
     * @param selector - a series of property names separated by the separator
     * @param separator - property separator ('.' by default)
     * @param errorMessage - the error message to dispatch in case the selector points to undefined data
     */
    selectInJson(selector, separator = '.', errorMessage = BindingHelper.ERROR_SELECTOR) {
        if (!selector) {
            return (source$) => source$;
        }
        const op = (source$) => source$.pipe(map((data) => selector.split(separator).reduce((acc, curr) => (acc ? acc[curr] : undefined), data)), tap((data) => {
            if (data === undefined) {
                throw new Error();
            }
        }), this.catchErrorAndDispatch(errorMessage));
        return isolateErrors(op);
    }
    /**
     * Will apply a function on each emission, or return EMPTY if function invocation fails for any reason.
     * The function will be passed the flow's emission, as well as the previous result of the function invocation.
     *
     * Errors will not be propagated downstream.
     *
     * @param fun - function as string
     */
    applyPostProcessor(fun) {
        if (!fun) {
            return (source$) => source$;
        }
        const op = (source$) => this.functionExecutionService.isValid(fun).pipe(switchMap((isValid) => {
            if (!isValid) {
                this.dispatchError(BindingHelper.ERROR_FUNCTION_SYNTAX);
                return EMPTY;
            }
            return source$.pipe(switchMap((data) => of([data, this.previousPostProcessedData])), this.functionExecutionService.run(fun), tap((postProcessedData) => (this.previousPostProcessedData = postProcessedData)), this.catchErrorAndDispatch(BindingHelper.ERROR_FUNCTION_RUNTIME));
        }));
        return isolateErrors(op);
    }
    /**
     * On error dispatches message, and resolves it if data was received + no error was thrown
     *
     * @param message - message to dispatch/resolve
     */
    catchErrorAndDispatch(message) {
        return snatchError(() => this.dispatchError(message), () => this.dispatchError(message, undefined, true));
    }
}
BindingHelper.ERROR_FUNCTION_SYNTAX = 'The post processor function you entered is not valid.\n\nPlease make sure there are no typos or JavaScript syntax errors.';
BindingHelper.ERROR_FUNCTION_RUNTIME = 'A Runtime error occurred. Please check and adapt the post processor function.';
BindingHelper.ERROR_SELECTOR = 'Data could not be retrieved.\n' +
    '\n' +
    'The path configuration of the REST Endpoint has changed. The data format has changed or the path is not available anymore. ' +
    'Please adapt the path or contact the admin of this REST Endpoint.';
BindingHelper.ERROR_JSON = `Unsuitable data format. Please make sure the provided data format is JSON.`;
BindingHelper.WARN_UPDATE_PERIOD = 'Unable to retrieve data at the specified rate.';
/**
 * On error calls the provided callback and sends an EMPTY downstream, and if no error was caught then onSuccess is called
 *
 * @param onError - called when an error occurs
 * @param onSuccess - called when there was no error to catch
 */
function snatchError(onError, onSuccess) {
    return (source$) => source$.pipe(catchError((e) => {
        onError(e);
        return of();
    }), tap(() => onSuccess()));
}
/**
 * Be aware that when using this
 * 1. the subscription's error handler will never be called if the error occurs before this operator.
 * 2. no operators after this one are called if the flow keeps causing errors.
 *
 * @param delayMs - delay between retry attempts in ms
 */
function retryWithDelay(delayMs) {
    return (source$) => source$.pipe(retryWhen((errors) => errors.pipe(delay(delayMs))));
}
/**
 * When using this, there are two effects wrt errors
 * 1. errors occurring from outside this operator aren't processed here
 * 2. errors occurring as a result of the given operator aren't propagated to the original observable's error handler.
 * Requires that operators do their own error handling.
 * The point of this is to create resilient flows that can recover from errors.
 *
 * Drawback: No state is transferred between operator invocations (i.e. scan, reduce, etc don't work)
 *
 * @param op - the operator to isolate errors of
 * @returns operator which wraps the provided operator
 */
function isolateErrors(op) {
    return (source$) => source$.pipe(switchMap((data) => of(data).pipe(op, catchError(() => of()))));
}

const IMAGE_DATA_PATTERN = /^data:image(\/[^;]+)?;base64,/;
function isValidBase64ImageData(imageData) {
    return IMAGE_DATA_PATTERN.test(imageData);
}

const permissionOrder = [DashboardPermission.Read, DashboardPermission.Write, DashboardPermission.Admin].reduce((acc, item, index) => {
    acc[item] = index;
    return acc;
}, {});
function hasPermission({ actualLevel, minLevel, }) {
    return permissionOrder[actualLevel] >= permissionOrder[minLevel];
}

/**
 * Generated bundle index. Do not edit.
 */

export { BestEffortSingleQueryTimer, BindingHelper, BindingStateComponent, DashboardBuilderCoreModule, ElementDefinitionService, ElementPropertyLinkingDirective, FunctionExecutionResultComponent, FunctionExecutionService, GridTileComponent, HelpStateComponent, NotificationService, ScrollIntoViewDirective, SpecializedBindingPropertiesComponent, SpecializedElementPropertiesComponent, UserInputComponent, hasPermission, isValidBase64ImageData, isolateErrors, retryWithDelay, snatchError };
